import {
  ACCESS_REQUEST_STATUS_TITLES,
  ACCESS_REQUEST_STATUSES,
  type AccessRequest,
  type AccessRequestStatus,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { useState } from 'react';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner, type Tone, toast } from '../../ui/feedback';
import { Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { CreateTenantDialog } from './TenantsPage';

const STATUS_TONE: Record<AccessRequestStatus, Tone> = {
  new: 'accent',
  contacted: 'info',
  created: 'success',
  rejected: 'neutral',
};

function RequestRow({ r, onCreate }: { r: AccessRequest; onCreate: () => void }) {
  const qc = useQueryClient();
  const [note, setNote] = useState(r.note ?? '');
  const [open, setOpen] = useState(false);
  const update = useMutation({
    mutationFn: (status: AccessRequestStatus) =>
      api(`/platform/requests/${r.id}`, { method: 'PATCH', body: { status, note: note.trim() || null } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform'] });
      qc.invalidateQueries({ queryKey: ['inbox'] });
      toast('Заявка обновлена');
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Не удалось сохранить', 'danger'),
  });
  return (
    <li className="flex flex-col gap-2 px-5 py-3">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-ink [overflow-wrap:anywhere]">{r.company}</span>
            <Badge tone={STATUS_TONE[r.status]}>{ACCESS_REQUEST_STATUS_TITLES[r.status]}</Badge>
          </div>
          <div className="text-[13px] text-ink-2">
            {r.name} ·{' '}
            <a
              className="inline-flex min-h-11 items-center underline-offset-2 hover:underline md:min-h-0"
              href={`mailto:${r.email}`}
            >
              {r.email}
            </a>{' '}
            ·{' '}
            <a
              className="inline-flex min-h-11 items-center underline-offset-2 hover:underline md:min-h-0"
              href={`tel:${r.phone.replace(/[^\d+]/g, '')}`}
            >
              {r.phone}
            </a>
          </div>
        </div>
        <span className="num text-[12px] text-ink-2">{dateTime(r.createdAt)}</span>
      </div>
      {r.interest ? <p className="text-[14px] whitespace-pre-line text-ink">{r.interest}</p> : null}
      {r.tenantId ? (
        <Link
          to="/platform/tenants/$id"
          params={{ id: r.tenantId }}
          className="text-[13px] font-medium text-ink underline underline-offset-2"
        >
          Открыть созданную компанию
        </Link>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        {r.status !== 'created' ? (
          <Button size="sm" variant="primary" onClick={onCreate}>
            Создать компанию
          </Button>
        ) : null}
        <Select
          aria-label="Статус заявки"
          value={r.status}
          onChange={(e) => update.mutate(e.target.value as AccessRequestStatus)}
          className="h-11 w-auto md:h-8"
          disabled={update.isPending}
        >
          {/* «Компания создана» ставит сам портал при создании компании из заявки — вручную не выбирается */}
          {ACCESS_REQUEST_STATUSES.filter((s) => s !== 'created' || r.status === 'created').map((s) => (
            <option key={s} value={s}>
              {ACCESS_REQUEST_STATUS_TITLES[s]}
            </option>
          ))}
        </Select>
        <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)}>
          {open ? 'Скрыть заметку' : r.note ? 'Заметка' : 'Добавить заметку'}
        </Button>
      </div>
      {open ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="О чём договорились, когда перезвонить"
          />
          <Button
            size="sm"
            variant="secondary"
            loading={update.isPending}
            onClick={() => update.mutate(r.status)}
          >
            Сохранить
          </Button>
        </div>
      ) : r.note ? (
        <p className="text-[13px] text-ink-2">Заметка: {r.note}</p>
      ) : null}
    </li>
  );
}

/** Заявки на доступ с главной страницы (ГЛ-04): связаться, создать компанию, отклонить */
export function RequestsPage() {
  const [status, setStatus] = useState<'new' | 'all'>('new');
  const q = useQuery({
    queryKey: ['platform', 'requests', status],
    queryFn: () => api<AccessRequest[]>('/platform/requests', { query: { status } }),
  });
  const [creating, setCreating] = useState<AccessRequest | null>(null);
  return (
    <>
      <Panel
        title="Заявки на доступ"
        meta="ГЛ-04"
        actions={
          <div
            className="flex rounded-md border border-line p-0.5"
            role="tablist"
            aria-label="Какие заявки показывать"
          >
            {(['new', 'all'] as const).map((s) => (
              <button
                key={s}
                type="button"
                role="tab"
                aria-selected={status === s}
                onClick={() => setStatus(s)}
                className={clsx(
                  'h-11 rounded-[5px] px-3 text-[13px] md:h-7',
                  status === s ? 'bg-ink text-on-ink' : 'text-ink-2 hover:text-ink',
                )}
              >
                {s === 'new' ? 'Новые' : 'Все'}
              </button>
            ))}
          </div>
        }
        bodyClassName="p-0"
      >
        {q.isPending ? (
          <div className="p-5">
            <Spinner />
          </div>
        ) : q.isError ? (
          <div className="p-5">
            <ErrorBox error={q.error} />
          </div>
        ) : !q.data.length ? (
          <div className="p-5">
            <EmptyState title={status === 'new' ? 'Новых заявок нет' : 'Заявок пока не было'}>
              Заявки приходят с формы «Запросить доступ» на странице входа. О каждой новой владельцы портала
              получают письмо и дело в «Ждёт меня».
            </EmptyState>
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {q.data.map((r) => (
              <RequestRow key={r.id} r={r} onCreate={() => setCreating(r)} />
            ))}
          </ul>
        )}
      </Panel>
      {creating ? <CreateTenantDialog request={creating} onClose={() => setCreating(null)} /> : null}
    </>
  );
}
